import * as THREE from "three";
import { COATS, PATTERNS } from "@catworld/shared";

/**
 * Live recolouring of a cat's shipped materials.
 *
 * The coats are baked images -- an atlas PNG for the generated cats, a jpeg
 * for the imported toons -- so colour is not a property that can be set.
 * Instead each coat and eye material gets one onBeforeCompile patch that
 * remaps the sampled texel in the fragment shader, and the sliders drive
 * uniforms.  apply() writes uniform values and nothing else: a recompile on a
 * slider `input` event is a visible hitch on every mouse move, so the patch
 * happens exactly once per material and customProgramCacheKey is pinned so
 * that all materials of a role share one program instead of compiling one
 * per patched closure.  The pattern picker rides the same rule: every
 * pattern is a branch on ONE integer uniform inside the one coat program, so
 * clicking through the chips never compiles anything either.
 *
 * The remap replaces `#include <map_fragment>`: after the texel lands in
 * diffuseColor, before any lighting.  Remapping later -- after the lighting
 * chunks -- recolours the shading along with the coat, which flattens the cat
 * into a decal of itself.
 *
 * Roles are matched on material name, by substring so both rig families land:
 *   coat  -- `pelt` (generated atlas), `Mat_Gradient` (toon jpeg)
 *   eye   -- `eye` (atlas, mapped), `eye_iris` (toon, flat baseColorFactor)
 *   pupil -- `eye_pupil`, deliberately untouched: a hue-rotated pupil reads
 *            as a rendering bug, not a custom cat.
 *
 * PATTERNS.  cats/gen/pelt.py states the authoring rule for the baked coats:
 * the pattern is a function of the 3D surface point, never of UV, so it can
 * cross seams and survive atlas re-layout.  The picked patterns obey the
 * same rule at runtime.  Each coat vertex gets two extra attributes baked at
 * patch time -- where on the CAT this vertex is -- and the fragment shader
 * evaluates the pattern from their interpolation:
 *
 *   catBody.xyz  bind-pose position in a normalized body box: x the cat's
 *                left (-1..1), y paws (0) to back (1), z tail root (0) to
 *                nose (1).  Raw geometry space IS this frame bar the
 *                normalisation, for both families: the inverse bind
 *                translations of both rigs put the paws at y~0 and the nose
 *                up +z (verified against sandy.glb and toon.glb), because
 *                gltf.py writes the generated cats that way and the toon's
 *                FBX-era axis flip lives on its `Rig` node, above the skin.
 *   catBody.w    bind-pose normal's up-ness (-1 belly .. 1 back), for the
 *                belly/bib masks.  Baked rather than read from vNormal
 *                because vNormal is the ANIMATED normal: a cat rolling over
 *                mid-pounce would watch its tuxedo bib slide around its
 *                barrel.  Bind-space values keep every mask glued to the fur.
 *   catPart      how far along a limb chain the vertex sits, from the skin
 *                weights: x legs (0 shoulder/hip .. 1 paw), y tail (0 root
 *                .. 1 tip), z head (0 neck .. 1 nose, ears ~0.9), w ears.
 *                Bone-derived because "is this a paw" is a question about
 *                the skeleton, not about height -- a long-legged look or the
 *                toon's stumpy proportions would defeat any geometric guess,
 *                while the weights are exactly what the artist already
 *                painted the answer into.
 *
 * The attributes are bind-pose constants, identical for every clone that
 * shares a geometry, so they are computed once per geometry and left in
 * place on dispose() -- roster.js clones share geometries across remote
 * cats, and stripping an attribute out from under another clone's live
 * program would be a torn-down stranger reaching into your cat.
 */

/**
 * All remapping happens on the gamma-encoded value, pow(1/2.2) in and
 * pow(2.2) back out.  The renderer decodes the sRGB textures to linear before
 * map_fragment runs, and linear is a bad space to slide colour around in: the
 * encoding packs most of the perceptual range below 0.22, so a linear-space
 * saturation lerp or luminance pivot is nearly inert across the dark half of
 * the coat and the hue slider feels dead on the shadow side.  A 2.2 power is
 * not the exact sRGB curve, but the remap goes encode -> shift -> decode, so
 * the mismatch cancels; what matters is working where equal steps look equal.
 */
const GAMMA = 2.2;

/** Rec.709 luma, applied to gamma-encoded values -- as CSS filters define it. */
const LUMA = [0.2126, 0.7152, 0.0722];

/**
 * `stripes` pivots luminance about the coat's own mid-tone, measured off the
 * texture at patch time.  A constant would be wrong by up to a third: the
 * shipped coats' painted means run 0.45 (toon-tabby) to 0.69 (sandy), because
 * pelt.py paints stripes darker and belly lighter *around each preset's base
 * colour*, not around mid-grey.  Pin sandy to 0.5 and stripes = 0 drops the
 * whole cat well below its base coat instead of flattening it onto it.  The
 * fallback only exists for headless runs, where nothing renders anyway.
 */
const PIVOT_FALLBACK = 0.55;
const PIVOT_SAMPLE = 128;      // readback size; the mean needs 2 figures, not 1024²

/**
 * `coat_light` is a straight multiply up to the knee, then a rational
 * shoulder that approaches 1 asymptotically.  A hard multiply at the slider's
 * max (1.55) clips belly, muzzle and base to the same white and the cat loses
 * its markings; with the shoulder a 0.90 belly and a 0.64 base stay apart
 * (0.95 vs 0.90) all the way up.  C1 at the knee, so no visible band.
 */
const LIGHT_KNEE = 0.8;

/**
 * emissiveIntensity at eye_glow = 1.  The iris albedo is ~0.2-0.5 linear, so
 * this adds about half the iris colour again on top of full daylight -- a
 * catch-light, clearly visible under the game's ACES curve but nowhere near
 * self-illuminating the face.  Past ~1 the eyes read as headlamps.
 */
const EYE_GLOW_MAX = 0.55;

/**
 * Swatch key -> gamma-space RGB.  The schema's hex IS the gamma-space value
 * -- the whole remap works on the sRGB-encoded side precisely so that a
 * colour a person picked off a screen means what they saw -- so this is a
 * parse, not a conversion.  "natural" carries null and apply() turns it into
 * tint amount 0 rather than into some fake neutral colour: there is no RGB
 * that means "whatever this base cat was painted".
 */
const COAT_TINT = new Map(COATS.map(({ key, hex }) => [
  key,
  hex && [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255),
]));

/**
 * Pattern key -> the integer the shader branches on.  Derived from PATTERNS
 * so the schema stays the single owner of the list; the shader's branch
 * constants are spliced from this same object, so a reorder in schema.js
 * cannot desynchronise data from code.  Everything below `tabby` -- natural
 * and solid -- draws no overlay, which is why the shader's outer guard is a
 * single >= against tabby's index.
 */
const PAT = Object.fromEntries(PATTERNS.map((p, i) => [p.key, i]));

const MAP_TOKEN = "#include <map_fragment>";
const EMISSIVE_TOKEN = "#include <emissivemap_fragment>";
const VERT_TOKEN = "#include <begin_vertex>";

/**
 * The CSS/SVG hue-rotate matrix: a rotation about the grey axis that
 * preserves Rec.709 luma, so turning the hue slider never lightens or darkens
 * the coat.  Built on the CPU per apply() -- 6 trig terms beat per-fragment
 * RGB->HSV->RGB, and a matrix has none of HSV's hexcone banding.
 */
function hueMatrix(deg, out) {
  const a = THREE.MathUtils.degToRad(deg);
  const c = Math.cos(a), s = Math.sin(a);
  return out.set(
    0.213 + 0.787 * c - 0.213 * s, 0.715 - 0.715 * c - 0.715 * s, 0.072 - 0.072 * c + 0.928 * s,
    0.213 - 0.213 * c + 0.143 * s, 0.715 + 0.285 * c + 0.140 * s, 0.072 - 0.072 * c - 0.283 * s,
    0.213 - 0.213 * c - 0.787 * s, 0.715 - 0.715 * c + 0.715 * s, 0.072 + 0.928 * c + 0.072 * s,
  );
}

const clamp01 = (x) => Math.min(1, Math.max(0, x));

const GLSL_LUMA = `const vec3 catLuma = vec3(${LUMA.join(", ")});`;

/**
 * The coat's vertex side exists only to hand the pattern attributes across
 * to the fragment stage.  They are read off the raw `position`-era
 * attributes, NOT the skinned output: the varyings must interpolate the
 * bind-pose values so the pattern deforms with the fur instead of the fur
 * sliding through a world-anchored pattern every step the cat takes.
 */
const COAT_VERT_DECL = `
attribute vec4 catBody;
attribute vec4 catPart;
varying vec4 vCatBody;
varying vec4 vCatPart;
`;

const COAT_VERT = `${VERT_TOKEN}
vCatBody = catBody;
vCatPart = catPart;`;

/**
 * Value noise over the body box, for spot blobs, calico patches and stripe
 * wobble.  A hash-sin lattice rather than anything fancier: the coordinates
 * it ever sees are 0..~20 (body box times pattern_scale times a small
 * frequency), which is far inside the range where sin-hash precision
 * artefacts appear, and at 8 taps per lookup it is the cheapest thing that
 * does not tile visibly on a cat-sized canvas.  Deliberately NOT shared with
 * textures.js's CPU-side pnoise: that one is periodic because level tiles
 * must meet themselves, and a cat is not a tile.
 */
const GLSL_NOISE = `
float catHash(vec3 p) {
  p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}
float catNoise(vec3 p) {
  vec3 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(mix(catHash(i),                     catHash(i + vec3(1, 0, 0)), f.x),
        mix(catHash(i + vec3(0, 1, 0)),     catHash(i + vec3(1, 1, 0)), f.x), f.y),
    mix(mix(catHash(i + vec3(0, 0, 1)),     catHash(i + vec3(1, 0, 1)), f.x),
        mix(catHash(i + vec3(0, 1, 1)),     catHash(i + vec3(1, 1, 1)), f.x), f.y),
    f.z);
}
`;

const COAT_DECL = `
uniform mat3 catHue;
uniform float catSat;
uniform float catLight;
uniform float catStripes;
uniform float catPivot;
uniform vec3 catTint;
uniform float catTintAmt;
uniform int catPat;
uniform float catPatAmt;
uniform float catPatScale;
varying vec4 vCatBody;
varying vec4 vCatPart;
${GLSL_LUMA}
${GLSL_NOISE}
`;

/**
 * The coat remap, in the order the operations earn:
 *
 *   1. baked contrast (`stripes`), about the measured pivot -- unchanged.
 *   2. the swatch: a colourize, not a hue-shift.  The texel collapses to
 *      one number -- its luma relative to the coat's pivot, i.e. the FUR
 *      DETAIL with the base colour divided out -- and the swatch colour is
 *      scaled by it.  This is what makes a swatch click dramatic where the
 *      hue slider was not: hue rotation preserves the coat's saturation, so
 *      a grey-brown cat could only ever become a differently-tinged
 *      grey-brown cat, while the colourize replaces chroma outright and a
 *      black swatch genuinely lands on black.  Markings survive as the
 *      detail ratio.  The pivot in the divisor is floored well above the
 *      near-black atlas background so a dark texel cannot blow the ratio up.
 *   3. the picked pattern, masks built from the body-frame varyings.  Every
 *      dark overlay is the LOCAL coat colour scaled down rather than a
 *      constant, so tabby stripes on a cream cat are warm brown and on a
 *      blue cat slate -- markings follow the coat the way real eumelanin
 *      does, and no swatch+pattern pair can produce clown colours.  The two
 *      exceptions are calico's patches, which are the point of calico.
 *      Every overlay keeps a slice of the detail ratio so the fur grain
 *      shows through the mask instead of reading as paint.
 *   4. hue / saturation / brightness refinement -- unchanged, and applied
 *      after the pattern so the fine sliders tune the FINISHED coat,
 *      overlays included, rather than diverging from them.
 *
 * Mask constants are tuned against the normalized body box, not metres, so
 * one set fits both families.  Regions in brief: belly is the down-facing
 * bind normal; sock lines are horizontal cuts in body-y gated by the
 * skeleton's own leg weights (so a low-slung chin or tail can never catch a
 * sock); the colourpoint face is the head-chain gradient widened by
 * pattern_scale from a muzzle dab to a full mask.  Both ends of every range
 * were eyeballed on both rig families -- the standard is the schema's: any
 * reachable combination is still a cat.
 */
const COAT_MAP = `${MAP_TOKEN}
{
  vec3 catC = pow(max(diffuseColor.rgb, vec3(0.0)), vec3(1.0 / ${GAMMA}));
  float catLum = dot(catC, catLuma);
  catC *= max(catPivot + (catLum - catPivot) * catStripes, 0.0) / max(catLum, 1e-4);
  float catDet = dot(catC, catLuma) / max(catPivot, 0.15);
  catC = mix(catC, catTint * catDet, catTintAmt);
  if (catPat != ${PAT.natural} && catPat != ${PAT.solid}) {
    vec3 catB = vCatBody.xyz;
    float catLeg = vCatPart.x, catTail = vCatPart.y, catHead = vCatPart.z, catEar = vCatPart.w;
    float catBelly = smoothstep(0.10, 0.65, -vCatBody.w);
    vec3 catWhite = vec3(0.94, 0.92, 0.88) * (0.78 + 0.22 * catDet);
    float catM = 0.0;
    vec3 catMC = catC;
    if (catPat == ${PAT.tabby}) {
      float limbw = smoothstep(0.30, 0.70, catLeg);
      float sc = mix(catB.z, 1.0 - catB.y, limbw);
      float ph = sc * 58.0 * catPatScale
               + 2.2 * abs(catB.x)
               + 2.8 * (catNoise(catB * vec3(3.0, 2.0, 5.0)) - 0.5);
      catM = smoothstep(0.15, 0.60, sin(ph))
           * (1.0 - 0.85 * catBelly)
           * (1.0 - smoothstep(0.35, 0.65, catHead));
      catMC = catC * 0.34;
    } else if (catPat == ${PAT.spots}) {
      float n = catNoise(catB * vec3(9.0, 6.0, 9.0) * catPatScale + 3.0);
      catM = smoothstep(0.585, 0.63, n) * (1.0 - 0.75 * smoothstep(0.66, 0.71, n))
           * (1.0 - 0.80 * catBelly)
           * (1.0 - smoothstep(0.55, 0.85, catHead));
      catMC = catC * 0.34;
    } else if (catPat == ${PAT.tuxedo}) {
      float bib = catBelly * (0.30 + 0.70 * smoothstep(0.30, 0.70, catB.z));
      float chin = smoothstep(0.78, 0.96, catHead) * smoothstep(0.02, 0.40, -vCatBody.w);
      float sockY = 0.12 * catPatScale;
      float sock = smoothstep(sockY + 0.05, sockY - 0.03, catB.y) * smoothstep(0.25, 0.50, catLeg);
      catM = max(max(smoothstep(0.25, 0.60, bib), chin), sock);
      catMC = catWhite;
    } else if (catPat == ${PAT.calico}) {
      vec3 cp = catB * vec3(3.6, 2.6, 3.6) * catPatScale;
      float po = smoothstep(0.54, 0.60, catNoise(cp + 11.0));
      float pk = smoothstep(0.56, 0.62, catNoise(cp + vec3(23.0, 5.0, 17.0))) * (1.0 - po);
      catC = mix(catC, vec3(0.85, 0.51, 0.19) * (0.70 + 0.30 * catDet), po * catPatAmt);
      catC = mix(catC, vec3(0.16, 0.13, 0.15) * (0.60 + 0.40 * catDet), pk * catPatAmt);
    } else if (catPat == ${PAT.point}) {
      float sockY = 0.16 * catPatScale;
      float paws = smoothstep(sockY + 0.06, sockY - 0.04, catB.y) * smoothstep(0.20, 0.45, catLeg);
      float lo = 0.95 - 0.28 * catPatScale;
      float face = smoothstep(lo, lo + 0.16, catHead);
      catM = max(max(paws, smoothstep(0.02, 0.40, catTail)),
                 max(face, smoothstep(0.35, 0.75, catEar)));
      catMC = catC * 0.26;
    } else if (catPat == ${PAT.socks}) {
      float sockY = 0.16 * catPatScale;
      catM = smoothstep(sockY + 0.05, sockY - 0.03, catB.y) * smoothstep(0.25, 0.50, catLeg);
      catMC = catWhite;
    }
    catC = mix(catC, catMC, catM * catPatAmt);
  }
  catC = max(catHue * catC, vec3(0.0));
  catC = max(mix(vec3(dot(catC, catLuma)), catC, catSat), vec3(0.0));
  catC *= catLight;
  vec3 catOver = max(catC - ${LIGHT_KNEE}, vec3(0.0));
  catC = min(catC, vec3(${LIGHT_KNEE})) + (1.0 - ${LIGHT_KNEE}) * catOver / (catOver + (1.0 - ${LIGHT_KNEE}));
  diffuseColor.rgb = pow(clamp(catC, 0.0, 1.0), vec3(${GAMMA}));
}`;

const EYE_DECL = `
uniform mat3 catEyeHue;
uniform float catEyeSat;
${GLSL_LUMA}
`;

const EYE_MAP = `${MAP_TOKEN}
{
  vec3 catC = pow(max(diffuseColor.rgb, vec3(0.0)), vec3(1.0 / ${GAMMA}));
  catC = max(catEyeHue * catC, vec3(0.0));
  catC = clamp(mix(vec3(dot(catC, catLuma)), catC, catEyeSat), 0.0, 1.0);
  diffuseColor.rgb = pow(catC, vec3(${GAMMA}));
}`;

/**
 * The glow has to follow the eye sliders or a re-hued iris glints in its old
 * colour.  This one runs in linear, skipping the gamma round-trip: the hue
 * matrix and the saturation lerp are linear maps that work at any magnitude,
 * and an additive catch-light does not need perceptual spacing.
 */
const EYE_EMISSIVE = `${EMISSIVE_TOKEN}
{
  vec3 catG = max(catEyeHue * totalEmissiveRadiance, vec3(0.0));
  totalEmissiveRadiance = max(mix(vec3(dot(catG, catLuma)), catG, catEyeSat), vec3(0.0));
}`;

/**
 * Mean gamma-space luma of the painted texels.  Canvas readback bytes are
 * sRGB-encoded -- already the (approximately) gamma space the shader chunk
 * works in, so the measured mean is directly comparable to its values.  The
 * generated atlases are islands on unpainted black, so near-black texels are
 * background, not coat, and are excluded; the toon jpegs have no background
 * and pass everything.  Headless (tools/inspect.mjs) has no canvas to read
 * back on and nothing to render, so the fallback constant is fine there.
 */
function measurePivot(texture) {
  const image = texture && texture.image;
  if (!image || typeof document === "undefined") return PIVOT_FALLBACK;
  try {
    const c = document.createElement("canvas");
    c.width = c.height = PIVOT_SAMPLE;
    const g = c.getContext("2d", { willReadFrequently: true });
    g.drawImage(image, 0, 0, PIVOT_SAMPLE, PIVOT_SAMPLE);
    const px = g.getImageData(0, 0, PIVOT_SAMPLE, PIVOT_SAMPLE).data;
    let sum = 0, n = 0;
    for (let i = 0; i < px.length; i += 4) {
      const lum = (LUMA[0] * px[i] + LUMA[1] * px[i + 1] + LUMA[2] * px[i + 2]) / 255;
      if (lum < 0.02) continue;
      sum += lum;
      n++;
    }
    return n ? sum / n : PIVOT_FALLBACK;
  } catch {
    return PIVOT_FALLBACK;     // ImageBitmap closed, tainted canvas, jsdom...
  }
}

/**
 * A bone name, family-blind: the same reduction morph.js uses (kept in step
 * by hand -- see its normName), so "foreleg_02", "leg.upper.F.L_014" and
 * "legupperFL" all answer the same classification questions.
 */
function boneKey(name) {
  return String(name || "").toLowerCase().replace(/_\d+$/, "").replace(/[._\s-]+/g, "");
}

/** Control bones -- IK targets, the toon's tail.CTRL -- carry no skin. */
const BONE_CONTROL = /ik|ctrl/;

/**
 * Is this bone part of a leg?  Checked BEFORE the head table because
 * "forearm" contains "ear": a substring scheme that tested ears first would
 * quietly paint colourpoints on the front elbows.
 */
const BONE_LEG = /clavicle|arm|cannon|paw|thigh|shin|leg|foot|toe/;

/**
 * Head-chain value per bone, most specific first.  The numbers are the
 * catPart.z gradient the face masks cut against: the muzzle/jaw end sits at
 * 1 so a colourpoint face can start as a dab on the nose, the skull at 0.72
 * so widening the mask swallows the head smoothly, the neck at 0.25 so no
 * face mask can bleed onto the shoulders.  Ears carry 0.9 here AND 1 in
 * their own channel: stripes key off head-ness (a tabby's ears are not
 * striped) while colourpoint ears need their own switch.
 */
const BONE_HEAD = [
  [/jaw|mouth|muzzle|nose|eye/, 1.0],
  [/ear/, 0.9],
  [/head|skull/, 0.72],
  [/neck/, 0.25],
];

/**
 * Bake the two pattern attributes onto a coat mesh's geometry.
 *
 * catBody is geometry normalisation: raw bind-pose position mapped into the
 * body box (see the class comment for why raw geometry space is already the
 * cat's own frame in both families).  catPart is skeleton work: each bone is
 * classified by name, leg and tail bones get a 0..1 fraction along their
 * OWN chain -- parent-walked, so the generated rig's five-bone forelegs and
 * the toon's three-bone ones both come out 0 at the body and exactly 1 at
 * the paw -- and each vertex blends those per-bone values through its skin
 * weights.  The weights are the artist's own answer to "which part is
 * this": a geometric guess (height, radius) breaks on the first look whose
 * leg_length slider moves, while the weights are morph-invariant.
 *
 * Idempotent by design: clones from roster.js share geometries, so the
 * second CatPelt over a geometry finds the attributes and leaves.  All of
 * it is bind-pose data -- there is nothing look-dependent to go stale.
 */
function bakePatternAttributes(mesh) {
  const geo = mesh.geometry;
  if (!geo || geo.getAttribute("catBody")) return;
  const pos = geo.getAttribute("position");
  const nrm = geo.getAttribute("normal");
  if (!pos) return;
  const n = pos.count;

  // ---- catBody: the normalized body box --------------------------------
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity,
      minZ = Infinity, maxZ = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  }
  const hw = Math.max(Math.abs(minX), Math.abs(maxX)) || 1;
  const sy = (maxY - minY) || 1, sz = (maxZ - minZ) || 1;
  const body = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    body[i * 4] = pos.getX(i) / hw;
    body[i * 4 + 1] = (pos.getY(i) - minY) / sy;
    body[i * 4 + 2] = (pos.getZ(i) - minZ) / sz;
    body[i * 4 + 3] = nrm ? nrm.getY(i) : 0;
  }
  geo.setAttribute("catBody", new THREE.BufferAttribute(body, 4));

  // ---- catPart: per-bone values blended by skin weight ------------------
  const part = new Float32Array(n * 4);       // zero: a torso everywhere
  const bones = mesh.skeleton && mesh.skeleton.bones;
  const skinI = geo.getAttribute("skinIndex");
  const skinW = geo.getAttribute("skinWeight");
  if (bones && skinI && skinW) {
    const kind = bones.map((b) => {
      const k = boneKey(b.name);
      if (BONE_CONTROL.test(k)) return null;
      if (BONE_LEG.test(k)) return "leg";
      if (k.includes("tail")) return "tail";
      return null;
    });
    const index = new Map(bones.map((b, i) => [b, i]));

    // Chain fraction: depth below the topmost same-kind ancestor, over the
    // chain's own deepest bone, off-by-one'd so a one-bone chain is 1 (a
    // paw, not a hip) and the deepest bone of any chain is exactly 1.
    const depth = new Array(bones.length).fill(-1);
    const chainRoot = new Array(bones.length).fill(-1);
    for (let i = 0; i < bones.length; i++) {
      if (!kind[i]) continue;
      let d = 0, r = i;
      for (let b = bones[i].parent; b && index.has(b); b = b.parent) {
        const j = index.get(b);
        if (kind[j] !== kind[i]) break;
        d++; r = j;
      }
      depth[i] = d;
      chainRoot[i] = r;
    }
    const chainMax = new Map();
    for (let i = 0; i < bones.length; i++) {
      if (depth[i] < 0) continue;
      chainMax.set(chainRoot[i], Math.max(chainMax.get(chainRoot[i]) || 0, depth[i]));
    }

    const legF = new Float32Array(bones.length);
    const tailF = new Float32Array(bones.length);
    const headF = new Float32Array(bones.length);
    const earF = new Float32Array(bones.length);
    for (let i = 0; i < bones.length; i++) {
      const k = boneKey(bones[i].name);
      if (BONE_CONTROL.test(k)) continue;
      if (depth[i] >= 0) {
        const f = (depth[i] + 1) / (chainMax.get(chainRoot[i]) + 1);
        if (kind[i] === "leg") legF[i] = f; else tailF[i] = f;
        continue;
      }
      for (const [re, v] of BONE_HEAD) {
        if (re.test(k)) { headF[i] = v; break; }
      }
      if (/ear/.test(k)) earF[i] = 1;
    }

    for (let i = 0; i < n; i++) {
      let leg = 0, tail = 0, head = 0, ear = 0, wsum = 0;
      for (let c = 0; c < 4; c++) {
        const j = skinI.getComponent(i, c);
        const w = skinW.getComponent(i, c);
        leg += w * legF[j];
        tail += w * tailF[j];
        head += w * headF[j];
        ear += w * earF[j];
        wsum += w;
      }
      // glTF weights sum to 1, but quantized exports drift a percent or two
      // and the masks cut hard thresholds, so re-normalise rather than let a
      // sock line waver with export settings.
      const inv = wsum > 1e-4 ? 1 / wsum : 0;
      part[i * 4] = leg * inv;
      part[i * 4 + 1] = tail * inv;
      part[i * 4 + 2] = head * inv;
      part[i * 4 + 3] = ear * inv;
    }
  }
  geo.setAttribute("catPart", new THREE.BufferAttribute(part, 4));
}

export class CatPelt {
  constructor(root) {
    this._saved = new Map();   // material -> pre-patch state, for dispose()
    this._flatIris = [];       // { material, base } -- recoloured on the CPU
    this._atlasEyes = [];      // materials whose glow is just emissiveIntensity
    this._scratch = new THREE.Vector3();

    // One uniform object per slider, shared by every patched program of the
    // role: onBeforeCompile wires these same references into shader.uniforms,
    // so apply() writes each value once and every mesh sees it.
    this._coatU = {
      catHue: { value: new THREE.Matrix3() },
      catSat: { value: 1 },
      catLight: { value: 1 },
      catStripes: { value: 1 },
      // The palette tint and the pattern selector.  catTint is a Vector3,
      // not a Color, deliberately: Color is subject to three's colour
      // management, and these components are already the gamma-space values
      // the shader chunk wants -- a round trip through working-space
      // conversion would quietly bend every swatch.
      catTint: { value: new THREE.Vector3(1, 1, 1) },
      catTintAmt: { value: 0 },
      catPat: { value: 0 },
      catPatAmt: { value: 1 },
      catPatScale: { value: 1 },
    };
    this._eyeU = {
      catEyeHue: { value: new THREE.Matrix3() },
      catEyeSat: { value: 1 },
    };

    // Meshes share material instances; collect first so each is patched once.
    // Coat-wearing meshes are kept too: the pattern attributes are a
    // per-geometry bake, not a per-material patch.
    const materials = new Set();
    const meshes = new Map();          // material -> [mesh, ...]
    root.traverse((o) => {
      if (!o.isMesh) return;
      for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
        materials.add(m);
        const list = meshes.get(m);
        if (list) list.push(o); else meshes.set(m, [o]);
      }
    });
    for (const m of materials) this._patch(m, meshes.get(m));
  }

  _patch(material, meshes) {
    const name = (material.name || "").toLowerCase();
    if (name.includes("pupil")) return;
    const eye = name.includes("eye") || name.includes("iris");
    const coat = !eye && (name.includes("pelt") || name.includes("gradient"));
    if (!eye && !coat) return;

    this._saved.set(material, {
      onBeforeCompile: material.onBeforeCompile,
      customProgramCacheKey: material.customProgramCacheKey,
      emissiveMap: material.emissiveMap,
      emissiveIntensity: material.emissiveIntensity,
      emissive: material.emissive.clone(),
      color: material.color.clone(),
    });

    if (coat) {
      // Every mesh wearing this coat gets the pattern attributes; the one
      // shared coat program (cache key below) reads them unconditionally.
      for (const mesh of meshes || []) bakePatternAttributes(mesh);
      // The pivot is per material (each coat has its own mid-tone) but not
      // per slider, so it rides along as an extra uniform, set once here.
      const pivot = { value: measurePivot(material.map) };
      this._wire(material, "catpelt-coat", COAT_DECL,
                 [[MAP_TOKEN, COAT_MAP]], { ...this._coatU, catPivot: pivot },
                 COAT_VERT_DECL, [[VERT_TOKEN, COAT_VERT]]);
    } else if (material.map) {
      // Atlas eye: iris, rim and pupil are all texels of one island, so the
      // glow needs the map -- white emissive x atlas means the bright iris
      // glints in its own colour and the near-black pupil stays dark.  Wired
      // now, at intensity 0, so turning eye_glow up later never flips the
      // USE_EMISSIVEMAP define and recompiles mid-drag.
      material.emissive.setRGB(1, 1, 1);
      material.emissiveMap = material.map;
      material.emissiveIntensity = 0;
      this._wire(material, "catpelt-eye", EYE_DECL,
                 [[MAP_TOKEN, EYE_MAP], [EMISSIVE_TOKEN, EYE_EMISSIVE]], this._eyeU);
      this._atlasEyes.push(material);
    } else {
      // Toon iris: a flat baseColorFactor, no texel to remap, so the same
      // gamma-space move runs on the CPU in apply() and lands on
      // material.color -- still only a uniform write, no patch needed.
      this._flatIris.push({ material, base: material.color.clone() });
    }
  }

  _wire(material, key, decl, edits, uniforms, vertDecl, vertEdits) {
    material.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, uniforms);
      let src = shader.fragmentShader;
      for (const [token, chunk] of edits) {
        const out = src.replace(token, chunk);
        // .replace() with a missing token is a silent no-op, and a silently
        // unpatched shader is a dead slider with no error anywhere: shout.
        if (out === src) console.error(`CatPelt: "${token}" not in fragment shader`);
        src = out;
      }
      shader.fragmentShader = decl + src;
      if (!vertEdits) return;
      // The coat's pattern varyings.  Written in GLSL1 keywords on purpose:
      // three's WebGL2 prelude #defines attribute/varying to in/out, so the
      // same chunk text works whichever backend compiled it.
      let vsrc = shader.vertexShader;
      for (const [token, chunk] of vertEdits) {
        const out = vsrc.replace(token, chunk);
        if (out === vsrc) console.error(`CatPelt: "${token}" not in vertex shader`);
        vsrc = out;
      }
      shader.vertexShader = vertDecl + vsrc;
    };
    material.customProgramCacheKey = () => key;
    material.needsUpdate = true;       // once, here -- never from apply()
  }

  /** Uniform (and material.color) writes only; never triggers a compile. */
  apply(look) {
    hueMatrix(look.coat_hue, this._coatU.catHue.value);
    this._coatU.catSat.value = look.coat_sat;
    this._coatU.catLight.value = look.coat_light;
    // "solid" is the one pattern implemented on the CPU side: it scales the
    // BAKED contrast toward flat by pattern_strength instead of drawing an
    // overlay, so at full strength the tabby toon really is plain, and
    // easing strength back down lets its own markings ghost back in.  The
    // Markings slider stays live throughout -- the two multiply.
    this._coatU.catStripes.value = look.pattern === "solid"
      ? look.stripes * (1 - look.pattern_strength)
      : look.stripes;

    const tint = COAT_TINT.get(look.coat);
    this._coatU.catTintAmt.value = tint ? 1 : 0;    // null = "natural"
    if (tint) this._coatU.catTint.value.set(tint[0], tint[1], tint[2]);
    // ?? 0 covers a pattern key from a future build reaching a stale client
    // mid-session; anything from storage or the wire has already been
    // normalized to a key this build knows.
    this._coatU.catPat.value = PAT[look.pattern] ?? 0;
    this._coatU.catPatAmt.value = look.pattern_strength;
    this._coatU.catPatScale.value = look.pattern_scale;

    hueMatrix(look.eye_hue, this._eyeU.catEyeHue.value);
    this._eyeU.catEyeSat.value = look.eye_sat;

    const glow = look.eye_glow * EYE_GLOW_MAX;
    for (const m of this._atlasEyes) m.emissiveIntensity = glow;

    for (const f of this._flatIris) {
      const v = this._scratch.set(
        Math.pow(f.base.r, 1 / GAMMA),
        Math.pow(f.base.g, 1 / GAMMA),
        Math.pow(f.base.b, 1 / GAMMA),
      ).applyMatrix3(this._eyeU.catEyeHue.value);
      const lum = LUMA[0] * v.x + LUMA[1] * v.y + LUMA[2] * v.z;
      const r = clamp01(lum + (v.x - lum) * look.eye_sat);
      const g = clamp01(lum + (v.y - lum) * look.eye_sat);
      const b = clamp01(lum + (v.z - lum) * look.eye_sat);
      f.material.color.setRGB(Math.pow(r, GAMMA), Math.pow(g, GAMMA), Math.pow(b, GAMMA));
      f.material.emissive.copy(f.material.color);
      f.material.emissiveIntensity = glow;
    }
  }

  /** Put every material back exactly as it was loaded.  The baked pattern
      attributes stay on the geometries on purpose: they are bind-pose
      constants shared by every clone of the geometry (see the class
      comment), and unreferenced attributes cost an unpatched program
      nothing. */
  dispose() {
    for (const [material, saved] of this._saved) {
      material.onBeforeCompile = saved.onBeforeCompile;
      material.customProgramCacheKey = saved.customProgramCacheKey;
      material.emissiveMap = saved.emissiveMap;
      material.emissiveIntensity = saved.emissiveIntensity;
      material.emissive.copy(saved.emissive);
      material.color.copy(saved.color);
      material.needsUpdate = true;     // drop the patched program
    }
    this._saved.clear();
    this._flatIris.length = 0;
    this._atlasEyes.length = 0;
  }
}
